import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchDownloaderInfo,
  fetchStatistic,
  fetchSystemInfo,
  fetchTransferStats,
  type DashboardSystemInfo,
  type DownloaderInfo,
  type Statistic,
} from "@/features/dashboard/api";
import { listSubscribes } from "@/features/subscribe/api";
import { listDownloading } from "@/features/download/api";
import { ApiError } from "@/shared/api/types";
import { formatBytes, formatDuration, formatSpeed } from "@/shared/lib/format";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";
import { Button } from "@/shared/ui/Button";

interface Board {
  statistic: Statistic | null;
  downloader: DownloaderInfo | null;
  system: DashboardSystemInfo | null;
  transferToday: number | null;
  activeSubs: number | null;
  downloading: number | null;
}

/** 今日总览 */
export function DashboardPage() {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statistic, downloader, system, transfer, subs, dls] =
        await Promise.all([
          fetchStatistic(),
          fetchDownloaderInfo(),
          fetchSystemInfo(),
          fetchTransferStats(1),
          listSubscribes(),
          listDownloading(),
        ]);
      setBoard({
        statistic,
        downloader,
        system,
        transferToday: transfer?.reduce((a, b) => a + (b || 0), 0) ?? 0,
        activeSubs: subs.filter((s) => s.state === "R").length,
        downloading: dls.length,
      });
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 403
          ? "需要超级用户权限才能查看仪表板"
          : e instanceof Error
            ? e.message
            : "加载失败";
      setError(msg);
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="今日"
        description="订阅 · 下载 · 整理 · 系统"
        actions={
          <Button variant="ghost" onClick={() => void load()} disabled={loading}>
            刷新
          </Button>
        }
      />

      {loading && !board ? (
        <div className="flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {board ? (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="订阅中"
              value={String(board.activeSubs ?? "—")}
              hint="状态为 R"
              to="/subscribe"
            />
            <StatCard
              label="下载中"
              value={String(board.downloading ?? "—")}
              hint={
                board.downloader
                  ? `↓ ${formatSpeed(board.downloader.download_speed)}`
                  : undefined
              }
              to="/download"
            />
            <StatCard
              label="今日整理"
              value={String(board.transferToday ?? "—")}
              hint="最近 1 天合计"
              to="/transfer"
            />
            <StatCard
              label="媒体库"
              value={
                board.statistic
                  ? `${board.statistic.movie_count ?? 0} / ${board.statistic.tv_count ?? 0}`
                  : "—"
              }
              hint="电影 / 剧集"
              to="/library"
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <section className="rounded-[var(--radius-md)] border border-border bg-bg-card p-4">
              <h2 className="text-[13px] font-medium text-fg-muted">下载器</h2>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
                <Row
                  k="下载速度"
                  v={formatSpeed(board.downloader?.download_speed)}
                />
                <Row
                  k="上传速度"
                  v={formatSpeed(board.downloader?.upload_speed)}
                />
                <Row
                  k="已下载"
                  v={formatBytes(board.downloader?.download_size)}
                />
                <Row
                  k="剩余空间"
                  v={formatBytes(board.downloader?.free_space)}
                />
              </dl>
            </section>
            <section className="rounded-[var(--radius-md)] border border-border bg-bg-card p-4">
              <h2 className="text-[13px] font-medium text-fg-muted">系统</h2>
              {board.system ? (
                <dl className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
                  <Row k="主机" v={board.system.hostname} />
                  <Row k="版本" v={board.system.version} />
                  <Row k="系统" v={board.system.operating_system} />
                  <Row k="运行" v={formatDuration(board.system.runtime)} />
                </dl>
              ) : (
                <EmptyState title="暂无系统信息" />
              )}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: string;
  hint?: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-[var(--radius-md)] border border-border bg-bg-card p-4 transition-colors hover:border-border-strong hover:bg-bg-hover"
    >
      <div className="text-[12px] text-fg-subtle">{label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-brand">
        {value}
      </div>
      {hint ? (
        <div className="mt-1 text-[12px] text-fg-subtle">{hint}</div>
      ) : null}
    </Link>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-fg-subtle">{k}</dt>
      <dd className="mt-0.5 text-fg">{v}</dd>
    </div>
  );
}
